import { UserOutlined } from '@ant-design/icons';
import type { DatiSessioneUtente } from '@framework/api-client';
import { useTranslation } from '@framework/i18n';
import { Avatar, Button, Typography } from 'antd';
import type { ButtonProps } from 'antd';
import styled from 'styled-components';
import { getLegalEntityInitials } from '../../components/legal-entity/legalEntityDisplay';
import { useSelectedLegalEntity } from '../../components/legal-entity/useSelectedLegalEntity';

const { Text } = Typography;

const StyledUserButton = styled(Button)`
  &&& {
    height: auto;
    &:focus-visible {
      outline: 2px solid ${props => props.theme.focusOutlineNegativeColor} !important;
      outline-offset: 0px !important;
      transition: all 0.2s;
    }
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  min-width: 0;
  max-width: 100%;
  align-items: center;
`;

const AvatarUser = styled(Avatar)`
  display: flex;
  flex: 0 0 38px;
  min-width: 0;
  background-color: ${props => props.theme.colorPrimary};
  .ant-btn:hover & {
    background-color: ${props => props.theme.colorPrimaryActive};
    .ant-avatar-string {
      background-color: ${props => props.theme.colorPrimaryActive};
    }
  }
  .ant-avatar-string {
    background-color: ${props => props.theme.colorPrimary};
  }
`;

const UserTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 270px;
  text-align: left;
  line-height: 1.3;
  padding: 0 8px;
`;

const EntityNameLine = styled(Text)`
  display: inline-block;
  color: inherit;
`;

const EntityVatNumberLine = styled(Text)`
  display: inline-block;
  flex: 0 1 auto;
  min-width: 0;
  color: inherit;
  font-size: 0.9em;
`;

export interface IFrontUserButtonProps extends Omit<ButtonProps, 'type'> {
  currentUser?: DatiSessioneUtente | null;
}

/**
 * Bottone personalizzato per il menu utente lato compilazione
 * Visualizza il nome, il ruolo e l'ufficio dell'utente loggato.
 */
export function FrontUserButton(props: IFrontUserButtonProps) {
  const { currentUser, ...buttonProps } = props;
  const { t } = useTranslation();
  const { selectedLegalEntity } = useSelectedLegalEntity();

  if (selectedLegalEntity) {
    const initials = getLegalEntityInitials(selectedLegalEntity.ragioneSociale);
    const name = selectedLegalEntity.ragioneSociale ?? '';
    const vatNumber = selectedLegalEntity.partitaIva
      ? t('front-user-menu.button.piva-label', 'P.IVA {{piva}}', {
          piva: selectedLegalEntity.partitaIva,
        })
      : null;

    return (
      <StyledUserButton
        aria-haspopup="menu"
        aria-label={t('front-user-menu.button.aria-label', 'Menu Utente')}
        size="large"
        type="primary"
        {...buttonProps}
      >
        <ContentWrapper>
          <AvatarUser size={38}>{initials}</AvatarUser>
          <UserTextWrapper>
            <EntityNameLine strong ellipsis title={name}>
              {name}
            </EntityNameLine>
            {vatNumber && (
              <EntityVatNumberLine ellipsis title={vatNumber}>
                {vatNumber}
              </EntityVatNumberLine>
            )}
          </UserTextWrapper>
        </ContentWrapper>
      </StyledUserButton>
    );
  }

  const name =
    (currentUser?.utenteLoggato?.nome ?? '') +
    ' ' +
    (currentUser?.utenteLoggato?.cognome ?? '');

  return (
    <StyledUserButton
      aria-haspopup="menu"
      aria-label={t('front-user-menu.button.aria-label', 'Menu Utente')}
      size="large"
      type="primary"
      icon={<UserOutlined aria-hidden />}
      {...buttonProps}
    >
      {name}
    </StyledUserButton>
  );
}
